import type { ReactNode } from 'react'
import { ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'

export interface BreadcrumbItem {
    label: ReactNode
    to?: string
    onClick?: () => void
}

export interface BreadcrumbProps {
    items: BreadcrumbItem[]
    separator?: ReactNode
    className?: string
    linkClassName?: string
    currentClassName?: string
}

const Breadcrumb = ({
    items,
    separator = <ChevronRight size={18} strokeWidth={1.8} className="text-text-grey" />,
    className,
    linkClassName,
    currentClassName,
}: BreadcrumbProps) => {
    if (items.length === 0) return null

    return (
        <nav aria-label="Breadcrumb" className={cn('w-fit', className)}>
            <ol className="flex items-center gap-2 flex-wrap">
                {items.map((item, index) => {
                    const isLast = index === items.length - 1
                    const key = typeof item.label === 'string' ? item.label : index
                    const baseLink = cn(
                        'title2medium text-text-grey transition hover:text-text-blue-200 hover:underline underline-offset-4',
                        linkClassName
                    )

                    let content: ReactNode
                    if (isLast) {
                        content = (
                            <span aria-current="page" className={cn('title2medium text-text-black', currentClassName)}>
                                {item.label}
                            </span>
                        )
                    } else if (item.to) {
                        content = (
                            <Link to={item.to} className={baseLink}>
                                {item.label}
                            </Link>
                        )
                    } else if (item.onClick) {
                        content = (
                            <button type="button" onClick={item.onClick} className={baseLink}>
                                {item.label}
                            </button>
                        )
                    } else {
                        content = <span className={cn('title2 text-text-grey', linkClassName)}>{item.label}</span>
                    }

                    return (
                        <li key={key} className="inline-flex items-center gap-2 min-w-0">
                            {content}
                            {!isLast && <span aria-hidden="true">{separator}</span>}
                        </li>
                    )
                })}
            </ol>
        </nav>
    )
}

export default Breadcrumb
